// Counter sale: sell parts over the counter (no truck, no work order to fill in).
// Pick or scan parts, set qty / price, take the payment — the invoice (receipt) is made and stock comes off the shelf.
import { useMemo, useState } from 'react';
import { api, useApi, navigate, dollars, toCents, centsInput, today, cleanPasted, payMethods, type Row } from '../lib';
import { Button, Modal, Field, Select, SearchPick, ScanButton, Icon, useToast } from '../ui';
import { rankParts } from '../partsuggest';
import { priceFromCost } from '../../shared/calc';
import { qtyUom } from '../../shared/units';

type Line = { part: Row; qty: string; price: string };

export function CounterSale({ onClose }: { onClose: () => void }) {
  const parts = useApi<Row[]>('/api/parts');
  const customers = useApi<Row[]>('/api/customers');
  const settings = useApi<Row>('/api/settings');
  const [cust, setCust] = useState('');
  const [q, setQ] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [paid, setPaid] = useState(true);
  const [method, setMethod] = useState('Cash');
  const [ref, setRef] = useState('');
  const [po, setPo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const toast = useToast();
  const found = useMemo(() => rankParts(parts.data || [], q, 8).items, [parts.data, q]);
  const priceOf = (p: Row) => p.price || priceFromCost(p.cost || 0, settings.data || {}) || 0;
  const add = (p: Row) => {
    setLines(ls => { const i = ls.findIndex(l => l.part.id === p.id); if (i >= 0) return ls.map((l, k) => k === i ? { ...l, qty: String(Number(l.qty || 0) + 1) } : l); return [...ls, { part: p, qty: '1', price: centsInput(priceOf(p)) }]; });
    setQ('');
  };
  const scanned = async (code: string) => {
    try { const { part } = await api<{ part: Row | null }>(`/api/parts/lookup?sku=${encodeURIComponent(code)}`); if (part) add((parts.data || []).find(x => x.id === part.id) || part); else toast(`No part with ${code}`, 'err'); }
    catch (e) { toast((e as Error).message, 'err'); }
  };
  const subtotal = lines.reduce((a, l) => a + Math.round(Number(l.qty || 0)) * toCents(l.price), 0);
  async function sell() {
    setErr('');
    if (!lines.length) { setErr('Add at least one part'); return; }
    if (lines.some(l => !(Number(l.qty) > 0))) { setErr('Every line needs a quantity'); return; }
    if (lines.some(l => !toCents(l.price))) { setErr('Every line needs a price'); return; }
    if (!paid && !cust) { setErr('Pick the customer to put it on their account (not paid now)'); return; }
    setBusy(true);
    let woId = '';
    try {
      const wo = await api<Row>('/api/counter-sale/start', { body: { customer_id: cust || null, po_number: po } });
      woId = wo.id;
      for (const l of lines) await api(`/api/work-orders/${wo.id}/parts`, { body: { part_id: l.part.id, qty: Math.round(Number(l.qty)), unit_price: toCents(l.price) } });
      const inv = await api<Row>(`/api/work-orders/${wo.id}/invoice`, { body: {} });
      if (paid) {
        const full = await api<Row>(`/api/invoices/${inv.id}`);
        const bal = full.invoice.total - (full.payments as Row[] || []).reduce((a: number, p: Row) => a + p.amount, 0);
        if (bal > 0) await api(`/api/invoices/${inv.id}/payments`, { body: { amount: bal, method, paid_on: today(), reference: ref } });
      }
      toast(`Sold — ${inv.number}${paid ? ' paid' : ' on account'}`);
      onClose(); navigate('/invoices/' + inv.id);
    } catch (e) {
      setErr((e as Error).message + (woId ? ' — the sale is saved as a work order; finish it there.' : ''));
      if (woId) setTimeout(() => navigate('/work-orders/' + woId), 2500);
    } finally { setBusy(false); }
  }
  return (
    <Modal title="Counter sale" onClose={onClose} wide>
      <div className="counter">
        <div className="form-body">
          <SearchPick label="Customer (empty = walk-in)" value={cust} onChange={setCust} placeholder="Type customer name or USDOT — or leave empty"
            items={(customers.data || []).map(c => ({ id: c.id, label: c.name, sub: c.usdot ? `USDOT ${c.usdot}` : undefined }))} />
          <Field label="PO # (optional)" name="po" value={po} onChange={e => setPo(e.target.value)} />
        </div>
        <div className="counter-search">
          <label className="search"><Icon name="search" size={16} /><input value={q} onChange={e => setQ(cleanPasted(e.target.value))} placeholder="Part #, name, barcode…" aria-label="Find part" autoFocus /></label>
          <ScanButton onCode={c => void scanned(c)} />
        </div>
        {q.trim().length >= 2 && <ul className="pick-list counter-found">{found.length ? found.map(p => (
          <li key={p.id}><button type="button" onClick={() => add(p)}><span><b>{p.name}</b><small>{p.sku} · bin {p.bin || '—'}</small></span>
            <span className="stock">{p.track_qty === 0 ? '' : `${qtyUom(p.stock, p.uom)} · `}{priceOf(p) ? dollars(priceOf(p)) : 'no price'}</span></button></li>))
          : <li className="muted pad">Not in inventory — add it in Inventory first.</li>}</ul>}
        <table className="batch-table counter-lines">
          <thead><tr><th>Part</th><th>Qty</th><th>Price each</th><th>Amount</th><th></th></tr></thead>
          <tbody>{lines.map((l, i) => <tr key={l.part.id}>
            <td><b>{l.part.name}</b><small className="muted"> {l.part.sku}</small>{l.part.track_qty !== 0 && Number(l.qty) > l.part.stock && <small className="danger"> · only {l.part.stock} on hand</small>}</td>
            <td><input className="qty-in" inputMode="numeric" value={l.qty} onChange={e => setLines(ls => ls.map((x, k) => k === i ? { ...x, qty: e.target.value.replace(/[^\d]/g, '') } : x))} aria-label={`Qty ${l.part.name}`} /></td>
            <td><span className="pe-money"><em>$</em><input inputMode="decimal" value={l.price} onChange={e => setLines(ls => ls.map((x, k) => k === i ? { ...x, price: e.target.value } : x))} aria-label={`Price ${l.part.name}`} /></span></td>
            <td>{dollars(Math.round(Number(l.qty || 0)) * toCents(l.price))}</td>
            <td><button type="button" className="icon-btn" title="Remove" onClick={() => setLines(ls => ls.filter((_, k) => k !== i))}><Icon name="trash" size={15} /></button></td></tr>)}
            {!lines.length && <tr><td colSpan={5} className="muted">Search or scan the parts above.</td></tr>}</tbody>
          <tfoot><tr><td colSpan={3}><b>Parts</b> <small className="muted">(sales tax and fees are added on the receipt like any invoice)</small></td><td><b>{dollars(subtotal)}</b></td><td /></tr></tfoot>
        </table>
        <div className="form-body">
          <label className="check full"><input type="checkbox" checked={paid} onChange={e => setPaid(e.target.checked)} /> Paid now</label>
          {paid && <><Select label="Method" name="method" value={method} onChange={e => setMethod(e.target.value)}>{payMethods().map(m => <option key={m}>{m}</option>)}</Select>
            <Field label={method === 'Check' ? 'Check #' : 'Reference (optional)'} name="ref" value={ref} onChange={e => setRef(e.target.value)} /></>}
          {!paid && <p className="form-note full">Goes on the customer's account as an unpaid invoice (their normal terms).</p>}
        </div>
        {err && <div className="error-box"><Icon name="alert" />{err}</div>}
        <div className="form-foot"><span className="spacer" /><Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button kind="primary" busy={busy} onClick={() => void sell()}>{paid ? `Sell & take payment` : 'Sell on account'}</Button></div>
      </div>
    </Modal>
  );
}
